import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';

export interface ChallengeCheckinEntry {
  completed: boolean;
  challengeTitle: string;
  challengeId: string;
}

export interface ChallengeCheckinData {
  date: string;
  completed: boolean;
  challengeTitle: string;
  challengeId: string;
  // All challenges for this day (for multi-challenge overlay)
  allChallenges: ChallengeCheckinEntry[];
}

export interface ChallengeCheckinMap {
  [date: string]: ChallengeCheckinData;
}

export interface ActiveChallenge {
  id: string;
  title: string;
}

export function useChallengeCheckins(days: number = 30) {
  const [checkinMap, setCheckinMap] = useState<ChallengeCheckinMap>({});
  const [activeChallenges, setActiveChallenges] = useState<ActiveChallenge[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCheckins = async () => {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
      const endDate = format(new Date(), 'yyyy-MM-dd');

      // Fetch active personal challenges
      const { data: challenges, error: challengeError } = await supabase
        .from('wellness_user_challenges')
        .select(`
          id,
          custom_title,
          status,
          wellness_challenge_templates (
            title,
            short_label
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (challengeError) {
        console.error('Error fetching challenges:', challengeError);
        setIsLoading(false);
        return;
      }

      if (!challenges || challenges.length === 0) {
        setCheckinMap({});
        setActiveChallenges([]);
        setIsLoading(false);
        return;
      }

      const challengeIds = challenges.map(c => c.id);
      const challengeMap = new Map<string, string>();
      const activeList: ActiveChallenge[] = [];
      
      challenges.forEach(c => {
        const template = c.wellness_challenge_templates as any;
        const title = c.custom_title || template?.short_label || template?.title || 'Challenge';
        challengeMap.set(c.id, title);
        activeList.push({ id: c.id, title });
      });

      setActiveChallenges(activeList);

      // Fetch checkins for these challenges
      const { data: checkins, error: checkinError } = await supabase
        .from('wellness_challenge_checkins')
        .select('id, user_challenge_id, checkin_date, completed')
        .in('user_challenge_id', challengeIds)
        .gte('checkin_date', startDate)
        .lte('checkin_date', endDate)
        .order('checkin_date', { ascending: true });

      if (checkinError) {
        console.error('Error fetching checkins:', checkinError);
        setIsLoading(false);
        return;
      }

      // Build map with multi-challenge support per day
      const map: ChallengeCheckinMap = {};
      
      checkins?.forEach(checkin => {
        const dateKey = checkin.checkin_date;
        const challengeTitle = challengeMap.get(checkin.user_challenge_id) || 'Challenge';
        
        const entry: ChallengeCheckinEntry = {
          completed: checkin.completed,
          challengeTitle,
          challengeId: checkin.user_challenge_id
        };

        if (!map[dateKey]) {
          // First challenge for this date
          map[dateKey] = {
            date: dateKey,
            completed: checkin.completed,
            challengeTitle,
            challengeId: checkin.user_challenge_id,
            allChallenges: [entry]
          };
        } else {
          // Add additional challenge to this date
          map[dateKey].allChallenges.push(entry);
          // Update primary completed status (true if any is completed)
          if (checkin.completed) {
            map[dateKey].completed = true;
          }
        }
      });

      setCheckinMap(map);
      setIsLoading(false);
    };

    fetchCheckins();
  }, [days]);

  return { checkinMap, activeChallenges, isLoading };
}
